"use client";
import React, {useContext, useEffect, useRef, useState} from "react";
import styles from "./WhitePaper.module.css";
import smoothscroll from "smoothscroll-polyfill";
import {SwapMainAppContext} from "../../context/SwapMainAppContext";
import goldBg from '../../static_files/buttonBackgroundGold.svg';
import img1 from "../../static_files/image1_PhotoGrid.png";
import img2 from "../../static_files/image2_PhotoGrid.png";
import img3 from "../../static_files/image3_PhotoGrid.png";
import arrowImg from "../../static_files/arrow-forward-long.svg";
import arrowImgHover from '../../static_files/arrow-forward-long-hover.svg'
// import RobotModal from "../Fleet/RobotModal";
import QuizModal from "./QuizModal";
import {PRISMAX_BACKEND_URL} from "../../configs/config";
import { Helmet } from 'react-helmet-async';

const LITEPAPER_PDF = "/paper/PrismaX Litepaper.pdf";  // Path to the downloadable PDF


// Mapping array: corresponds to sections 1-7 in sidebar order
const sectionOrder = [
    "introduction",
    "teleoperation",
    "data",
    "robots-and-models",
    "product-offering",
    "roadmap",
    "appendix-a",
    "appendix-b"
];
export default function WhitePaper() {
    const pdfRef = useRef(null);
    const [isHovered, setIsHovered] = useState(false);
    const [selectedSection, setSelectedSection] = useState("");

    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => setIsHovered(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const [quizButtonHover, setQuizButtonHover] = useState(false);
    const [showQuizModal, setShowQuizModal] = useState(false);
    const [hasCompletedQuiz, setHasCompletedQuiz] = useState(false);
    const [quizPoints, setQuizPoints] = useState(0);
    const {
        shrinkLeftPanel,
        showNotification,
        userEmail,
        walletAddress,
        setShowPanel,
        userId
    } = useContext(SwapMainAppContext);


    useEffect(() => {
        if (userEmail !== '' || walletAddress !== '') {
            setIsAuthenticated(true);
        } else {
            setIsAuthenticated(false);
        }
    }, [userEmail, walletAddress]);
    
    // Check if user has already completed the quiz
    useEffect(() => {
        const checkQuizStatus = async () => {
            if (!userId) {
                return;
            }

            try {
                const response = await fetch(`${PRISMAX_BACKEND_URL}/api/quiz/check-status?userid=${userId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    }
                });

                const data = await response.json();
                if (data.success && data.data.completed) {
                    setHasCompletedQuiz(true);
                    setQuizPoints(data.data.points_earned);
                }
            } catch (error) {
                console.error('Error checking quiz status:', error);
            }
        };

        checkQuizStatus();
    }, [userId]);
    // ******* KEY PART: Correct multi-page slicing *******
    const handleDownloadLitepaper = () => {
        // Option A: use an <a download> tag (see returned JSX below)
        // Option B: create an <a> element via JS and trigger click:
        const link = document.createElement("a");
        link.href = LITEPAPER_PDF;
        link.download = "PrismaX_Litepaper.pdf"; // File name when saved locally
        link.click();
    };

    const handleCloseQuizModal = () => {
        setShowQuizModal(false)
    }
    const handleOpenQuizModal = () => {
        setShowQuizModal(true)
    }
    useEffect(() => {
        smoothscroll.polyfill();
        
        // Handle hash navigation on initial load
        const handleInitialHash = () => {
            if (window.location.hash) {
                const hash = window.location.hash.substring(1);
                const element = document.getElementById(hash);
                if (element) {
                    setTimeout(() => {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        setSelectedSection(hash);
                    }, 300);
                }
            }
        };
        
        if (document.readyState === 'complete') {
            handleInitialHash();
        } else {
            window.addEventListener('load', handleInitialHash);
            return () => window.removeEventListener('load', handleInitialHash);
        }
    }, []);

    const handleLogin = () => {
        setShowPanel(true)
    }
    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY;
            const offset = 120;

            let currentSection = sectionOrder[0];
            for (let i = 0; i < sectionOrder.length; i++) {
                const sectionId = sectionOrder[i];
                const elem = document.getElementById(sectionId);
                if (elem) {
                    const top = elem.offsetTop;
                    if (scrollY >= top - offset) {
                        currentSection = sectionId;
                    } else {
                        break;
                    }
                }
            }
            setSelectedSection(currentSection);
        };

        // Listen to scroll events
        window.addEventListener("scroll", handleScroll);
        // Cleanup: remove listener on component unmount
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    const handleSidebarClick = (targetId) => {
        // If the requested section is already selected, simply return
        if (selectedSection === targetId) return;

        // Find current and target indices
        const currentIndex = sectionOrder.indexOf(selectedSection);
        const targetIndex = sectionOrder.indexOf(targetId);

        // If not found, select target immediately and return
        if (currentIndex === -1 || targetIndex === -1) {
            setSelectedSection(targetId);
            return;
        }

        // step = +1 (forward) or -1 (backward)
        const step = targetIndex > currentIndex ? 1 : -1;
        let i = currentIndex;

        // Interval sequence used for animated sidebar scrolling
        const intervals = [50, 250, 90, 150, 50, 30];
        let idx = 0; // Tracks which interval index to use

        // Recursive setTimeout helper
        function doNextStep() {
            // Move one section forward or backward
            i += step;
            setSelectedSection(sectionOrder[i]);

            // Stop when the target section is reached
            if (i === targetIndex) return;

            // Switch to next interval value
            idx = (idx + 1) % intervals.length;

            // Call doNextStep after the chosen interval
            setTimeout(doNextStep, intervals[idx]);
        }

        // Kick-off the first timer using intervals[idx]
        setTimeout(doNextStep, intervals[idx]);
    };
    return (<div className={styles.pageWrapper} ref={pdfRef}>
        <Helmet>
            <title>PrismaX Whitepaper - The Future of AI-Powered Robotics</title>
            <meta property="og:title" content="PrismaX Whitepaper - The Future of AI-Powered Robotics" />
            <meta property="og:description" content="Discover how PrismaX is revolutionizing the robotics industry with advanced teleoperation, AI-driven data solutions, and innovative robotic fleets." />
            <meta property="og:image" content="https://app.prismax.ai/whitepaper%20thumbnail.svg" />
            <meta property="og:type" content="article" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="PrismaX Whitepaper - The Future of AI-Powered Robotics" />
            <meta name="twitter:description" content="Discover how PrismaX is revolutionizing the robotics industry with advanced teleoperation, AI-driven data solutions, and innovative robotic fleets." />
            <meta name="twitter:image" content="https://app.prismax.ai/whitepaper%20thumbnail.svg" />
        </Helmet>
        <div className={styles.container}>

            {/* Sidebar Navigation */}
            <aside className={`${styles.sidebar} ${shrinkLeftPanel ? styles.shrunkSidebar : ''}`}>

                <ul className={styles.sidebarList}>
                    <div className={styles.sidebarTitle}>Index</div>
                    <a href="#introduction">
                        <li onClick={() => handleSidebarClick("introduction")}
                            className={selectedSection === "introduction" ? styles.selected : ""}>
                            1. Introduction
                        </li>
                    </a>

                    <a href="#teleoperation">
                        <li onClick={() => handleSidebarClick("teleoperation")}
                            className={selectedSection === "teleoperation" ? styles.selected : ""}>
                            2. Teleoperation
                        </li>
                    </a>

                    <a href="#data">
                        <li onClick={() => handleSidebarClick("data")}
                            className={selectedSection === "data" ? styles.selected : ""}>
                            3. Data
                        </li>
                    </a>

                    <a href="#robots-and-models">
                        <li onClick={() => handleSidebarClick("robots-and-models")}
                            className={selectedSection === "robots-and-models" ? styles.selected : ""}>
                            4. Robots and Models
                        </li>
                    </a>

                    <a href="#product-offering">
                        <li onClick={() => handleSidebarClick("product-offering")}
                            className={selectedSection === "product-offering" ? styles.selected : ""}>
                            5. Product Offering
                        </li>
                    </a>

                    <a href="#roadmap">
                        <li onClick={() => handleSidebarClick("roadmap")}
                            className={selectedSection === "roadmap" ? styles.selected : ""}>
                            6. Roadmap
                        </li>
                    </a>


                </ul>

                <button
                    className={styles.loginButton}
                    onClick={handleOpenQuizModal}
                    style={{
                        backgroundImage: `url(${goldBg})`,
                        display: 'flex',
                        flexDirection: 'row',
                        width: '190px',
                        height: '38px',
                        fontSize: '16px',
                        borderRadius: '10px',
                        paddingInline: '8px'
                    }}
                    onMouseEnter={() => setQuizButtonHover(true)}
                    onMouseLeave={() => setQuizButtonHover(false)}
                >{hasCompletedQuiz ? 'Review answers' : 'Start Quiz'} <img src={quizButtonHover ? arrowImgHover : arrowImg} alt="→"/>

                </button>
            </aside>

            {/* Download Button */}
            {/*<Image*/}
            {/*    onMouseEnter={handleMouseEnter}*/}
            {/*    onMouseLeave={handleMouseLeave}*/}
            {/*    onClick={handleDownloadLitepaper}*/}
            {/*    src={isHovered ? DOWN_LOAD_BUTTON_HOVER : DOWN_LOAD_BUTTON}*/}
            {/*    alt="Download Arrow Icon"*/}
            {/*    width={200}*/}
            {/*    height={36}*/}
            {/*    style={{marginRight: '8px'}}*/}
            {/*    className={styles.downloadButton}*/}
            {/*/>*/}

            {/* Main Content */}
            <div
                className={`${styles.mainContentOuterContainer} ${shrinkLeftPanel ? styles.shrunkMainContentOuterContainer : ''}`}>
                {!isAuthenticated ? (
                    <div className={styles.loginContainer}>
                        <h1 className={styles.loginTitle}>Hey there!</h1>
                        <p className={styles.loginText}>
                            Login to access our whitepaper, complete quizzes, and earn Prisma Points for
                            expanding your robotics knowledge.
                        </p>
                        <button
                            className={styles.loginButton}
                            onClick={handleLogin}
                            style={{backgroundImage: `url(${goldBg})`}}
                        >Login Now
                        </button>
                    </div>
                ) : (
                    <main className={styles.mainContent}>
                        <div className={styles.mobileQuizButtonContainer}>
                            <button
                                className={styles.loginButton}
                                onClick={handleOpenQuizModal}
                                style={{
                                    backgroundImage: `url(${goldBg})`,
                                    display: 'flex',
                                    flexDirection: 'row',
                                    width: '100%',
                                    height: '44px',
                                    fontSize: '16px',
                                    borderRadius: '10px',
                                    paddingInline: '8px'
                                }}
                                onMouseEnter={() => setQuizButtonHover(true)}
                                onMouseLeave={() => setQuizButtonHover(false)}
                            >{hasCompletedQuiz ? 'Review answers' : 'Start Quiz'} <img src={quizButtonHover ? arrowImgHover : arrowImg} alt="→"/>
                            </button>
                        </div>
                        <div className={styles.title}>PrismaX: Open Standards for People and Robots</div>
                        <div className={styles.subTitle}>
                            PrismaX Team, July 8, 2025
                        </div>
                        <div id="introduction" style={{height:'40px'}}></div>

                        {/* 1. Introduction */}
                        <div className={styles.section}>
                            <div className={styles.sectionTitle}>1. Introduction</div>
                            <div className={styles.paragraph}>
                                Since 2010, significant progress has been made in general-purpose robotics
                                hardware. Technologies that were once bleeding edge, such as dexterous
                                manipulators and quasi-direct-drive actuators, are now commonplace and available
                                from dozens of vendors. As a result, mainstream adoption of robots is limited not by
                                technology or hardware design, but by largely human factors - things like
                                teleoperator training and management, data availability and diversity, and capital
                                and ownership for robot fleets.
                            </div>
                            <div className={styles.paragraph}>
                                PrismaX is building the open coordination layer to connect teleoperators, robot
                                users, fleet owners, and robotics companies. Operators can connect with users and
                                fleet owners to teleoperate robots, completing real-world tasks while collecting
                                extremely high-value data. Users can connect with operators and fleet owners to
                                request real-life services such as manufacturing logistics, and advertising. Robotics
                                companies can connect with fleet owners who are interested in investing in fleets
                                for data collection, allowing them to bootstrap their manufacturing and data without
                                needing dilutive capital.
                            </div>
                            <div className={styles.paragraph}>
                                The network accrues value by building communities of highly skilled operators, and
                                by collecting huge robotics datasets which are sold to robotics and AI companies. All
                                this is backed by protocol-level automatic validation and scoring based on data
                                traces, as well as through robust mechanisms such as staking and guild formation.
                                This allows the network to scale quickly, discover value, and display emergent
                                behavior not possible with any centralized equivale
                            </div>
                        </div>
                        <div id="teleoperation" style={{height:'40px'}}></div>
                        {/* 2. Teleoperation */}
                        <div className={styles.section}>
                            <div className={styles.sectionTitle}>2. Teleoperation</div>
                            <div className={styles.paragraph}>
                                In robotics, teleoperation (“teleop”) refers to any sort of human-controlled robot.
                                This could be as straightforward as a rover which is piloted by a human driver to
                                avoid obstacles, or as complex as an immersive VR system with a second set of
                                robot arms for force feedback. The “tele” in teleop is also merely a suggestion;
                                oftentimes, the robot and the operator are in the same room with line of sight
                                2
                                visibility. What’s important is that the robot directly copies human commands,
                                eliminating much of the uncertainty in automated systems.
                            </div>
                            <div className={styles.paragraph}>
                                Teleoperation can be used for:
                            </div>
                        </div>

                        <div className={`${styles.section} ${styles.innerSection}`}>

                            <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                <span className={styles.bullet}/>
                                Collecting high-quality data traces for model training. Teleop has a number of
                                advantages - strong baseline quality, single and known hardware
                                embodiment, and additional sensor data - that decrease time-to-convergence
                                for AI model training. As of this writing (mid-2025) most robotics AI
                                companies are using teleop as a key part of their training pipeline.
                            </div>


                            <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                <span className={styles.bullet}/>
                                Increasing the accuracy of robotics models. It is difficult, perhaps impossible,
                                to cross the 90% accuracy threshold for foundation models. 1-in-10 failures
                                would be unacceptable in a production system that interacts with the
                                physical world. LLMs are only 90% accurate, if that, but there are humans in
                                the loop in most applications to mitigate the errors; teleop acts as the
                                human-in-the-loop for robotics.
                            </div>
                        </div>

                        <div className={styles.paragraph}>
                            Existing robotics companies are unable to scale teleoperator fleets because of high
                            costs of management, unfamiliarity with local communities, and trust and quality
                            issues. PrismaX’s teleop protocol leverages automated trace quality assessment via
                            the Eval Engine (Appendix A) along with tried-and-tested structures such as guilds
                            and staked validators to allow for decentralized, horizontally scalable fleets while
                            ensuring trust and quality at the local level. More precisely:
                        </div>

                        <div className={`${styles.section} ${styles.innerSection}`}>

                            <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                <span className={styles.bullet}/>
                                The goal of the PrismaX teleop protocol is to optimally connect teleoperators
                                around the world with robots that need operating. As teleoperators work, the
                                network accrues a trace of their experience, the quality of their work, and the
                                tasks they are best at.
                            </div>

                            <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                <span className={styles.bullet}/>
                                Operators can optionally stake network tokens. Staking increases trust and
                                increases the chance they will receive the highest-payout work. Stakers earn
                                yield related not just to their stake, but to their quality of work as well as a
                                bonus for increased productivity over time.
                            </div>

                            <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                <span className={styles.bullet}/>
                                Payout for task completion earns a multiplicative bonus based on how fast
                                the work was completed. This quick return bonus (QRB) encourages
                                dedicated operators and allows the network to discover highly skilled
                                community members.
                            </div>

                            <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                <span className={styles.bullet}/>
                                The QRB is not just a protocol-level mechanism; data traces collected from
                                the fastest operators are especially valuable for model training, so AI
                                companies will pay a premium for the resulting data.
                            </div>

                            <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                <span className={styles.bullet}/>
                                Staking incentives are designed to encourage local guild formation. Guilds
                                are a powerful tool for coordinating labor at the smallest scales, improving
                                overall quality. The QRB also encourages guild formation because the highest
                                payout tasks and the highest throughput will require specialized hardware
                                such as VR headsets or motion controllers.
                            </div>

                            <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                <span className={styles.bullet}/>
                                The QRB and staking are both optional. The Eval Engine grants the protocol
                                Sibil resistance without the need for human validators, which allows
                                individuals to participate with just a computer and a keyboard. This helps
                                bootstrap the network through its initial growth stages (where the network’s
                                tokens are worth relatively little).
                            </div>
                        </div>

                        <div className={styles.paragraph}>
                            Most teleop token activity will be transactional - a fee-based marketplace where
                            robotics companies can find operators for their robots. Staking yields mint new
                            tokens (accruing value representing the stakers work in organizing and training
                            community members, possibly themselves). In addition, task completion mints new
                            tokens based on the improvement over time of the community member, regardless
                            of stake. Because the performance of an operator can only improve finitely, this
                            effectively adds a fixed number of tokens to the supply for each new operator
                            joining, mirroring how one of the network’s most valuable assets are its community
                            of trained operators.
                        </div>
                        <div id="data" style={{height:'40px'}}></div>

                        {/* 3. Data */}
                        <div className={styles.section}>
                            <div className={styles.sectionTitle}>3. Data</div>

                            <div className={styles.paragraph}>
                                Teleop accrues data which can be used to train AI models. The PrismaX data
                                marketplace encompasses:
                            </div>
                            <div className={`${styles.section} ${styles.innerSection}`}>

                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>
                                    Network-owned data, owned by the community and sold to
                                    multiple customers via a token-burn mechanism
                                </div>


                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>
                                    Customer-owned data, which is controlled by a single customer
                                </div>
                            </div>
                            <div className={styles.paragraph}>
                                Network owned data mints new tokens based on its Eval Engine score. Additionally,
                                at point of access, some of the transaction fees are not burned, but rather are
                                redistributed to the originators of the data. Customer-owned data is transacted
                                through a fee-based marketplace; transacted tokens are redistributed to the
                                originators based on the amount of data (we assume because a customer paid for
                                the the data, they value each minute the same; exactly how and what the data
                                contributes to the model’s knowledge is unimportant to the network).
                            </div>

                            <div className={styles.paragraph}>
                                Customer-owned data is a necessary practicality in the network’s early stage,
                                mirroring how robotics companies as of this writing (mid-2025) seem to prefer to
                                pay more (sometimes, egregiously more) to own their data. As the network’s
                                dataset becomes more powerful and robots are made commercially available, we
                                expect customer-owned data to be less necessary, and the protocol’s economics
                                and fee schedule reflects this - customers will be encouraged to look for the data
                                they need in the network-owned dataset first. Long term, the network’s value
                                comes from the fact that it effectively owns the Common Crawl for the real world,
                                allowing controlled access with financial attribution and giving the community a
                                significant influence over the future development of AI.
                            </div>

                            <div className={styles.paragraph}>
                                Because the Eval Engine is fully automated, the network is able to collect another
                                data modality - extremely large-scale visual task data. On a long horizon, visual task
                                data is tautologically equivalent to jointly collected teleop data for training purposes
                                - many of the other explicit sensor traces such as actuator angles can be inferred
                                from visual data (Appendix B). Visual data is the only modality which is arbitrarily
                                scalable: it can be passively collected as community members complete real-world
                                tasks in their lives, does not require manufacturing specialized hardware, and is not
                                limited by the number of available robots.
                            </div>

                            <div className={styles.paragraph}>
                                Ownership of visual data is sovereign to the network; collecting visual data mints
                                new tokens according to its Eval Engine score and accessing the dataset burns
                                tokens. Conceptually, access control to network data is done by having originators
                                control distribution; any slice of the dataset is worth less than its proportionate
                                fraction of the dataset, so data contributors are not incentivized to sell their data
                                outside of the marketplace. Practically speaking, the protocol will use a ‘fat node’
                                staking mechanism for data access; while in principle any contributor can become a
                                data storage node, the requirements for becoming a data node are set high enough
                                so that only committed players can participate. This guarantees quality of service
                                and implementation efficiency while still remaining cost-effective (through
                                decentralization) and compliant with various regulations.
                            </div>
                            <div className={styles.paragraph}>

                                Finally, the practical benefits of decentralized data are obvious. In order to achieve
                                full generalization, robotics models will need to be trained on Internet-scale data.
                                Companies are paying millions of dollars to collect even a small fraction of this data
                                right now; decentralization allows them to collect much more data, much more
                                quickly, for less money. At scale, the network’s unprecedented abilities will allow
                                model companies to iterate on data at the speed of code, which will be a paradigm
                                shift in how models are trained.
                            </div>
                        </div>

                        <div id="robots-and-models"  style={{height:'40px'}}></div>

                        {/* 4. Robots and models */}
                        <div className={styles.section}>
                            <div className={styles.sectionTitle}>4. Robots and Models</div>
                            <div className={styles.paragraph}>
                                PrismaX partners with pioneering robotics manufacturers and physical AI foundation
                                model builders to accelerate the adoption of intelligent robots serving the real
                                economy. Our approach creates a self-reinforcing cycle of improvement: as more
                                robots are deployed within our current teleoperation-powered network, they
                                generate increasingly valuable datasets that enhance physical AI intelligence,
                                ultimately improving robot productivity and economic viability.
                            </div>

                            <div className={styles.paragraph}>
                                The robotics industry currently faces a fundamental bootstrapping problem. Without
                                sufficiently effective AI models, robot ownership lacks economic justification for
                                most potential users. This chicken-and-egg scenario forces robotics companies to
                                invest in first-party fleets dedicated solely to data collection—a capital-intensive
                                approach that typically requires dilutive funding and diverts resources from core
                                product development
                            </div>

                            <div className={styles.paragraph}>
                                The PrismaX incentive mechanism breaks this cycle through an innovative incentive
                                mechanism that fundamentally reframes robot ownership economics. Viewed
                                through our framework, a robot functions as a network miner—an investment
                                vehicle that generates network tokens with potential future appreciation. This
                                approach provides multiple immediate revenue streams.
                            </div>

                            <div className={`${styles.section} ${styles.innerSection}`}>

                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>
                                    <strong>Premium Data Generation: </strong> Premium Data Generation: Robots
                                    immediately
                                    begin generating ROI
                                    through extremely high-quality data collection, currently valued at $30-50
                                    per hour by robotics companies.
                                </div>


                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>
                                    <strong>Flexible Monetization Options: </strong> Robot owners can work directly with
                                    data
                                    customers to provide custom datasets while collecting transaction fees,
                                    passively mine network tokens during idle periods, or complete revenue-
                                    generating tasks while simultaneously collecting valuable data
                                </div>

                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>
                                    <strong>Integrated Ecosystem Benefits: </strong> The network provides skilled
                                    operators,
                                    eliminating the need for owners to develop in-house expertise
                                </div>
                            </div>

                            <div className={styles.paragraph}>
                                Our system recognizes that participants often fulfill multiple roles, creating
                                additional value through:
                            </div>
                            <div className={`${styles.section} ${styles.innerSection}`}>

                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>
                                    <strong>Owner-Customer Integration: </strong>When robot owners are also data
                                    customers, they receive preferential pricing for their data needs.
                                </div>


                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>
                                    <strong>Owner-Operator Synergy: </strong>Robot owners who also serve as operators
                                    receive enhanced incentives for their operational time
                                </div>

                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>
                                    <strong>Network Effect Amplification:</strong> Each additional role increases both
                                    individual returns and overall network value
                                </div>
                            </div>

                            <div className={styles.paragraph}>
                                This comprehensive approach delivers:
                            </div>
                            <div className={`${styles.section} ${styles.innerSection}`}>

                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>
                                    <strong>Immediate Economic Viability:</strong> Robots generate returns from day one
                                    through data monetization
                                </div>


                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>
                                    <strong>Reduced Capital Risk:</strong> Multiple revenue streams and token
                                    appreciation
                                    potential offset initial investment
                                </div>

                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>
                                    <strong>Scalable Growth:</strong> Network effects create exponential value as more
                                    participants join
                                </div>
                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>
                                    <strong>Accelerated AI Development: </strong>Continuous, high-quality data flow
                                    improves
                                    AI models across the entire ecosystem
                                </div>
                            </div>

                            <div className={styles.paragraph}>
                                This innovative model transforms robot ownership from a capital-intensive gamble
                                into a diversified investment opportunity that drives immediate returns while
                                contributing to the broader advancement of physical AI capabilities. Economically,
                                robots-as-miners take advantage of the floating value of the network tokens to
                                encourage early ownership of hardware, helping the industry bootstrap. As robotics
                                6
                                enter the mainstream, robot owners will shift away from pure token-mining models
                                towards revenue-generating models; robot users are free to hire robots through the
                                network to complete real tasks (robot users are just data customers which return
                                their data to the network in exchange for lower prices). If a particular user believes
                                they can generate a particularly valuable or unique data modality, they are of
                                course welcome to retain ownership of the data in exchange for higher prices.
                            </div>

                            <div  className={styles.paragraph}>
                                AI models, of course, tie everything together. Even non-sovereign robotics models
                                increase the network’s value by enabling better robots and more robot adoption.
                                Models improve autonomy, which increases teleoperator efficiency by allowing one
                                operator to control multiple robots (if a model works 80% of the time, one operator
                                can control five robots, and so on). This amplifies the human capacity of the
                                network, allowing even more data collection, which in turn drives even better
                                models. PrismaX will also partner with open-source modeling teams to build robotics
                                models which are owned by the network; conceptually speaking, robotics
                                foundation models act as a sort of “digital teleoperator” which connects to the same
                                teleop infrastructure to “earn” tokens, which are immediately burned to deflate the
                                supply.
                            </div>
                        </div>
                        <div id='product-offering' style={{height:'40px'}}></div>

                        {/* 5. The PrismaX Product Offering */}
                        <div className={styles.section}>
                            <div className={styles.sectionTitle}>5. The PrismaX Product Offering</div>
                            <div className={styles.paragraph}>
                                Realized as a practical product, the three pillars above become:
                            </div>
                            <div className={`${styles.section} ${styles.innerSection}`}>

                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>
                                    An open-source, de facto standard teleop tech stack. By providing ready-to-
                                    run code and APIs that robot builders can build against, we encourage
                                    adoption through technology reuse. The robot-side ‘backend’ connects to our
                                    cross-platform frontend, which allows people around the world to operate
                                    robots from their browser or through VR. New frontends leveraging novel
                                    interaction hardware are also welcome to play. Underlying the stack is
                                    PrismaX’s blockchain protocol, which handles operator discovery, reputation,
                                    and compensation.
                                </div>


                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>
                                    A programmable, distributed data collection and filtering engine backed by
                                    tens of thousands (or more) of real humans who can collect new data on
                                    demand. Robotics companies can interact with the existing data set,
                                    programming their own filters to purchase only the data they want, or they
                                    can send API requests to acquire new data. The Eval Engine acts as both a
                                    programmable, real time filter as well as a validation mechanism for data
                                    requests.
                                </div>

                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>
                                    A three-sided marketplace where robot users, robot owners, and
                                    teleoperators can interact to transparently and fairly do business. The
                                    programmable marketplace enables use cases such as data collection, robot
                                    rentals, robots-for-hire, and, ultimately, intermachine coordination and robot-
                                    to-robot transactions.
                                </div>
                            </div>

                            <div  className={styles.paragraph}>
                                Backing the entire ecosystem is the $PIX token, which most ecosystem participants
                                (everyone but the robot owners and teleoperators) interact with as tradeable
                                service reservations with floating spot pricing. Reservations for network services are
                                made through decentralized clearing houses; in order to cold-start the ecosystem,
                                there will be an initial first-party clearing house, but fundamentally, anyone can
                                become one. Clearing houses are legally bound to be trustworthy (they are
                                businesses which sell a service to a customer) and disputes are settled the normal
                                way.
                            </div>
                        <div id="roadmap" style={{height:'10px'}}></div>

                        </div>

                        {/* 6. Roadmap */}
                        <div className={styles.section}>
                            <div className={styles.sectionTitle}>6. Roadmap</div>
                            <div className={styles.paragraph}>
                                We envision PrismaX evolving alongside the robotics industry:
                            </div>
                            <div className={`${styles.section} ${styles.innerSection}`}>

                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>
                                    <strong>Stage&nbsp;I </strong> will focus on teleop and visual data for model
                                    training.
                                    At this stage,
                                    robotics adoption is not yet widespread, and teleoperators are inexperienced.
                                    However, the per-hour value of teleop is higher, since robotics companies are
                                    willing to pay tens of dollars per hour for training data. Current reinforcement
                                    learning models for teleop often rely on simulator-based retargeting of
                                    human motion data, inevitably introducing gaps between training
                                    environments and real-world robot deployment scenarios. Effective
                                    community-driven collection of robotic action data through PrismaX will
                                    progressively bridge this sim-to-real gap and enhance policy network
                                    performance. In order to bootstrap the hardware distribution problem, guilds
                                    will be able to invest in robots that can be teleoperated. Stage I players are
                                    investing in the future value of the robotics industry, and in turn help that
                                    industry grow by solving the chicken-and-egg problem that plagues robotics
                                    today.
                                </div>


                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>
                                    <strong>Stage&nbsp;II </strong> operator fleets will be operating robots in the
                                    field to
                                    complete real
                                    tasks for real customers, not just for research and development. At this
                                    stage, operators and guild managers will be much more experienced in the
                                    nuances of teleoperation, allowing them to be competitive in the real-world
                                    labor marketplace. The robotics industry will also have ramped up
                                    manufacturing somewhat, and there will be tens of thousands of robots “in
                                    the wild”. Sufficient data will have been collected during Stage I to build edge
                                    prediction models that compensate for teleop latency, allowing for faster
                                    operators. Visual data collection will gain momentum in Stage II, enabling
                                    upcoming foundation models, and some robotics companies will have
                                    achieved a moderate degree of autonomy from Stage I data.
                                </div>

                                <div id='appendix-a' className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>
                                    <strong>Stage&nbsp;III </strong> will see robots achieve high degrees of autonomy as
                                    foundation
                                    models trained on data from Stage I and Stage II materialize. At this point,
                                    the network will shift towards providing production-level services for millions
                                    of robots, with a focus on cost and efficiency. The network will also provide
                                    8
                                    decentralized inference for its sovereign robotics foundation models, allowing
                                    for value capture at all points in the robotics pipeline. The network and token
                                    will enable other parts of the robot economy as well, such as machine-to-
                                    machine and human-to-machine transactions.
                                </div>
                            </div>
                        </div>

                        {/* Appendix A: The Eval Engine */}
                        <div className={styles.section}>
                            <div className={styles.sectionTitle}>Appendix&nbsp;A: The&nbsp;Eval&nbsp;Engine</div>
                            {/* Intro */}
                            <div className={styles.paragraph}>
                                PrismaX enables scalable visual data collection with the Eval Engine, which uses
                                state-of-the-art open-weight AI models to score images and video. The Eval Engine
                                generates quality scores between 0.0 and 1.0 representing the predicted value of
                                data, which can be used for downstream incentivization and filtering. These quality
                                scores are generated from data features extracted by AI models, which are made
                                publicly available as proof that the work to collect the data has been done.
                            </div>

                            {/* Quality Score Basics */}
                            <div className={`${styles.paragraph} ${styles.subparagraph}`}>Quality Score
                                Basics
                            </div>
                            <div className={styles.paragraph}>
                                In the data-focused Eval Engine implementation, quality scores are written as the
                                product of factors, each between 0.0 and 1.0. Factors represent mutually
                                orthogonal, equally weighted aspects of data quality; each factor is further
                                decomposed as the weighted average of subcomponents between 0.0 and 1.0,
                                which represent (somewhat) mutually exclusive measures of value in a given
                                category. In order to align the relative importance of the subcomponents of a factor
                                F with data user priorities, we apply a simple exponential moving average to learn
                                the weights over time:
                            </div>
                            <div className={`${styles.section} ${styles.innerSection}`}>

                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>

                                    <span className={styles.bullet}/> F = ∑ (W<sub>i</sub> × F<sub>i</sub>)
                                </div>
                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/> The weights W<sub>i</sub> are updated periodically
                                    as
                                    follows:
                                </div>
                                <div className={`${styles.section} ${styles.innerSection2}`}>
                                    <div className={`${styles.paragraph} ${styles.dotParagraph2}`}>
                                        <span className={styles.bullet}/> They are initialized to some values
                                        W<sub>i,0</sub> such
                                        that ∑ W<sub>i,0</sub> = 1
                                    </div>
                                    <div className={`${styles.paragraph} ${styles.dotParagraph2}`}>
                                        <span className={styles.bullet}/> Every N data accesses, W′<sub>i,k+1</sub> = ∑
                                        F<sub>i</sub> / (∑ F<sub>0</sub> + ∑ F<sub>1</sub> + …)
                                    </div>
                                    <div className={`${styles.paragraph} ${styles.dotParagraph2}`}>
                                        <span className={styles.bullet}/> W<sub>i,k+1</sub> = α × W<sub>i,k</sub> + (1 −
                                        α)
                                        × W′<sub>i,k+1</sub>
                                    </div>
                                </div>
                            </div>

                            {/* Feature Extraction */}
                            <div className={`${styles.paragraph} ${styles.subparagraph}`}>Feature Extraction</div>
                            <div className={styles.paragraph}>In the initial implementation, features are extracted
                                by multiple models, signed by a
                                trusted environment, and presented publicly as proof. As the protocol evolves,
                                features will transition to a single multi-task embedding, eliminating the need for a
                                trusted signer. More precisely, we extract:
                            </div>

                            <div className={`${styles.section} ${styles.innerSection}`}>
                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/> <strong>Frame embeddings:</strong> For each frame
                                    of
                                    the video, we compute the CLIP-L
                                    and DINOv2 embeddings of the image
                                </div>
                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/> <strong>Corrected motion field:</strong> We
                                    extract
                                    the optical flow for even-numbered
                                    frames, which is a WxHx3 tensor containing (&lt;direction, magnitude&gt;) vectors
                                    for each pixel in the frame. Directions are already normalized and
                                    magnitudes are normalized against the image diagonal. We then fit and
                                    remove global motion - translation, rotation, and dilation terms - leaving an
                                    estimate of the intraframe motion of each pair of adjacent frames. To check if
                                    the global motion estimate is meaningful, we compute the RMS flow magnitude across
                                    the
                                    frame before and after the global compensation; if the
                                    RMS value is not at least 20% lower, we skip global estimation.
                                </div>
                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/> <strong>Prediction errors:</strong> the flow from
                                    the
                                    even-numbered frames is interpolated
                                    to predict the odd-numbered frames. The prediction error frame is a measure
                                    of the motion consistency of the video.

                                </div>
                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/> <strong>Semantic embeddings:</strong> Using a
                                    standard
                                    late-fusion (image
                                    encoder &gt; projector &gt;  language model) vision-language model, we extract the
                                    final
                                    layer hidden state for the  &lt;/IMG&gt; token as the content-aware embedding of a
                                    frame. We fine-tune the vision-language model using a contrastive objective
                                    for retrieval, following the methods used for text embeddings.
                                </div>
                            </div>

                            <div className={styles.paragraph}>
                                These embeddings are used to compute quality scores based on simple scoring
                                heads. The idea is that it is at least as hard to synthesize an embedding with a high
                                score as it is to AI generate content, which is exceedingly difficult for long (> 5
                                second) videos. The signature is required to ensure that all embeddings come from
                                the same video (otherwise, an adversary could mix and match embeddings to
                                create artificially high-scoring proofs).
                            </div>

                            {/* Score Components */}
                            <div className={`${styles.paragraph} ${styles.subparagraph}`}>Score Components</div>
                            <div className={styles.paragraph}>
                                The score components are multiplied to generate the final quality score:
                            </div>
                            {/* Motion */}
                            <div className={`${styles.section} ${styles.innerSection}`}>
                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/><strong>The Motion </strong> score represents the
                                    amount of motion in a video.

                                </div>
                                <div className={`${styles.section} ${styles.innerSection2}`}>
                                    <div className={`${styles.paragraph} ${styles.dotParagraph2}`}>

                                        <span className={styles.bullet}/> Motion1:The percentile rank (against existing
                                        validated data) of the mean RMS corrected flow magnitude between adjacent frame
                                        pairs. Represents intraframe motion, which is highly desirable.

                                    </div>
                                    <div className={`${styles.paragraph} ${styles.dotParagraph2}`}>
                                        <span className={styles.bullet}/> Motion2: Interframe dilations are represented
                                        as a
                                        scaling factor. The differences between adjacent pairs of scaling factors are
                                        summed, and the percentile rank of the highest partial sum through the video is
                                        the
                                        Motion2 score. Motion2 captures videos with extended camera motion orthogonal to
                                        the
                                        camera, which are valuable for navigation and mapping.

                                    </div>
                                    <div className={`${styles.paragraph} ${styles.dotParagraph2}`}>
                                        <span className={styles.bullet}/> The Motion score combines these subcomponents
                                        with
                                        W_1 = 0.75, W_2 = 0.25, N = 100,000, alpha = 0.9.

                                    </div>
                                </div>
                            </div>

                            {/* Semantic */}
                            <div className={`${styles.section} ${styles.innerSection}`}>
                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/><strong>The Semantic </strong>score represents the
                                    value of the content the video depicts.
                                </div>

                                <div className={`${styles.section} ${styles.innerSection2}`}>
                                    <div className={`${styles.paragraph} ${styles.dotParagraph2}`}>
                                        <span className={styles.bullet}/>The Semantic score is based on an MLP that
                                        pools
                                        the semantic embedding into a raw score semantic_raw.

                                    </div>
                                    <div className={`${styles.paragraph} ${styles.dotParagraph2}`}>
                                        <span className={styles.bullet}/> As no such MLP exists, we need to train one.
                                        To
                                        cold-start the training process, we use the same vision-language model used to
                                        generate the semantic embeddings to classify a randomly-selected subset of
                                        LAION-5B
                                        with aesthetic score greater than 5.0 into one of 5 buckets corresponding to
                                        scores
                                        of [0.0, 0.2], [0.2, 0.4], etc. We set the score equal to the midpoint of each
                                        bucket, then train the MLP on the resulting synthetic dataset.

                                    </div>
                                    <div className={`${styles.paragraph} ${styles.dotParagraph2}`}>
                                        <span className={styles.bullet}/> Our training goal is to make the score
                                        monotonic:
                                        higher semantic scores mean the image is more likely to be selected.

                                    </div>
                                    <div className={`${styles.paragraph} ${styles.dotParagraph2}`}>
                                        <span className={styles.bullet}/>We use a contrastive learning objective: for
                                        each
                                        minibatch, we penalize out-of-order pairs (number of accesses versus score).
                                    </div>

                                    <div className={`${styles.paragraph} ${styles.dotParagraph2}`}>
                                        <span className={styles.bullet}/>Finally, Semantic is the percentile rank of
                                        semantic_raw among currently validated data.
                                    </div>
                                </div>
                            </div>

                            {/* Aesthetic */}
                            <div className={`${styles.section} ${styles.innerSection}`}>
                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/><strong>The Aesthetic </strong> score is based on
                                    an
                                    MLP which pools the CLIP-L embedding of each frame. This covers the general lighting
                                    and
                                    composition of the frame, but is also informative about blur, noise, and clarity.
                                </div>

                                <div className={`${styles.section} ${styles.innerSection2}`}>
                                    <div className={`${styles.paragraph} ${styles.dotParagraph2}`}>
                                        <span className={styles.bullet}/> Aesthetic1 is the percentile rank (against
                                        existing validated data) of the geometric mean of the framewise aesthetic score
                                        across all frames in the video.
                                    </div>

                                    <div className={`${styles.paragraph} ${styles.dotParagraph2}`}>
                                        <span className={styles.bullet}/>Aesthetic2 is the percentile rank (against
                                        existing
                                        validated data) of the geometric mean of the framewise aesthetic score across
                                        the
                                        top 1% scoring frames in the video.
                                    </div>
                                    <div className={`${styles.paragraph} ${styles.dotParagraph2}`}>
                                        <span className={styles.bullet}/> The Aesthetic score combines these
                                        subcomponents
                                        with W_1 = 0.25, W_2 = 0.75, N = 100,000, alpha = 0.9.
                                    </div>
                                    <div className={`${styles.paragraph} ${styles.dotParagraph2}`}>
                                        <span className={styles.bullet}/>Aesthetic1 and Aesthetic2 are necessary because
                                        of
                                        motion blur: a video could have high aesthetic appeal, but sufficient motion
                                        that
                                        many of its frames are blurry. Aesthetic2 selects for frames that do not have
                                        ‘technical’ deficiencies, and Aesthetic1 acknowledges the additional effort
                                        required
                                        for sharp videos which are valuable for some tasks.
                                    </div>
                                </div>
                            </div>


                            {/* Diversity */}
                            <div className={`${styles.section} ${styles.innerSection}`}>
                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/><strong> The Diversity</strong> score uses the
                                    distance
                                    between the semantic embeddings and their nearest neighbors to measure how novel new
                                    data submissions are.

                                </div>

                                <div className={`${styles.section} ${styles.innerSection2}`}>
                                    <div className={`${styles.paragraph} ${styles.dotParagraph2}`}>
                                        <span className={styles.bullet}/> The raw diversity score diversity_raw is the
                                        geomean of the cosine distances between a new datum and its k-nearest neighbors
                                        in
                                        the existing validated data.

                                    </div>

                                    <div id='appendix-b' className={`${styles.paragraph} ${styles.dotParagraph2}`}>
                                        <span className={styles.bullet}/>The final Diversity score is diversity_raw
                                        normalized against the diameter of the dataset in feature space.
                                    </div>

                                </div>
                            </div>
                        </div>


                        {/* Appendix B: Unsupervised Training and Feature Extraction */}
                        <div className={styles.section}>
                            <div className={styles.sectionTitle}>
                                Appendix&nbsp;B: Unsupervised Training&nbsp;and&nbsp;Feature&nbsp;Extraction
                            </div>

                            <div className={styles.paragraph}>
                                Large-scale unsupervised training (training on unlabeled data) is the heart of modern
                                generative AI. In this Appendix, we briefly review the intuition behind unsupervised
                                training
                            </div>

                            <div className={`${styles.paragraph} ${styles.subparagraph}`}>Training Objectives</div>

                            <div className={styles.paragraph}>
                                If we don’t label the data, how do we train? The crux here is to give the model a
                                trivial
                                task (the training objective) that forces it to learn a deep understanding of recurring
                                patterns found in the training data. For example:
                            </div>
                            <div className={`${styles.section} ${styles.innerSection}`}>
                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>
                                    <strong>Autoregressive generation (next‑token prediction):</strong> predicting the
                                    next
                                    word or data point in a sequence (GPT3, OpenAI 4o image generation, most other
                                    language
                                    models)
                                </div>
                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>
                                    <strong>Masked modeling:</strong> predicting missing patches in images, or missing
                                    words
                                    in sentences (BERT, MAGVIT, DINO)

                                </div>
                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>
                                    <strong>Image compression:</strong> compressing an image or video into a fixed data
                                    size,
                                    or by a fixed amount (VQ-VAE and similar)
                                </div>
                            </div>

                            <div className={styles.paragraph}>
                                These simple tasks generate features that are incredibly powerful:
                            </div>

                            <div className={`${styles.section} ${styles.innerSection}`}>
                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>
                                    Text models trained on next-token prediction on the Internet famously exhibit
                                    in-context
                                    learning (if the initial sequence consists of a few examples, they tend to generate
                                    similar samples as they continue the sequence). They can also be fine-tuned (trained
                                    on
                                    a small number of samples of a specific format) to answer questions.

                                </div>
                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>
                                    Mask modeling as a standalone objective is useless. However, the resulting features
                                    are
                                    highly representative of the meaning of the underlying text - they can be used for
                                    sentiment analysis, summarization, question answering, and more. Likewise, image and
                                    video features from masked modeling are very strong drivers for downstream tasks
                                    such as
                                    segmentation, classification, and depth extraction.

                                </div>
                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>
                                    Compression is itself a useful task, but oftentimes, the emergent features
                                    correspond to
                                    physically meaningful concepts in images and video.

                                </div>
                            </div>

                            {/* What are features? */}
                            <div className={`${styles.paragraph} ${styles.subparagraph}`}>So, What Are Features?</div>

                            <div className={styles.paragraph}>
                                We’ve been talking about features for a while without actually explaining what they are.
                                Features are an abstract representation of the most important information in a dataset.
                                Good
                                features have the following hallmarks:
                            </div>

                            <div className={`${styles.section} ${styles.innerSection}`}>
                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>Compact: the size of the feature should be as small
                                    as
                                    possible, while still being able to capture all of the important information in the
                                    data.

                                </div>
                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>Semantically meaningful: mathematically important
                                    components of the features should correspond to physically (or linguistically,
                                    logically, etc.) important concepts in the data.
                                </div>
                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>Clustered: data which are ‘similar’ in some way
                                    should
                                    exist ‘close to each other’ in the feature space. Likewise, data which are
                                    dissimilar to
                                    each other should be ‘far away’. Distinct clusters of data in feature space should
                                    correspond to semantically meaningful data categories.
                                </div>
                                <div className={`${styles.paragraph} ${styles.dotParagraph}`}>
                                    <span className={styles.bullet}/>Linear: this one is not always possible, but
                                    ideally,
                                    ‘half-way’ between two features should correspond to some concept of ‘half-way-ness’
                                    between two data points.
                                </div>
                            </div>

                            {/* DINOv2 example */}
                            <div className={styles.paragraph}>
                                These representations are usually vectors or matrices, in which case mathematical
                                underpinnings of the above hallmarks are straightforward: the dimension of the vectors
                                should be minimal, each component should correspond to something meaningful about the
                                data,
                                similar data should correspond to vectors pointing in the same direction, and ideally,
                                interpolating down an axis of the vectors should correspond to some consistent change in
                                the
                                data itself.
                            </div>

                            <div className={styles.paragraph}>
                                This is all a bit confusing to talk about in the abstract, so let’s take a look at some
                                examples. One of my favorites is DINOv2, an image foundation model that does not get as
                                much
                                attention as it should. DINOv2 is trained using a clever and somewhat complicated
                                objective:
                                predicting that different crops of an image come from the same image, and guessing
                                hidden
                                patches masked away in an image. As a standalone task, this objective is useless, but it
                                forces the model to learn very general features. For example, DINOv2 generates features
                                for
                                every 14x14 patch of pixels in the image. What if we plot the principal components of
                                these
                                features?
                            </div>
                            <div className={styles.imageContainer}>
                                <img
                                    src={img1}
                                    alt="img1"
                                    style={{objectFit: "contain", width: '100%'}}
                                />
                            </div>
                            <div className={styles.paragraph}>

                                We get some striking emergent behavior. The black background is computed by thresholding
                                the
                                first principal component, which turns out to correspond to the main subject of the
                                image.
                                The colors visualize the next three components, which correspond to progressively finer
                                semantic features in the image. That’s already pretty neat - after all, DINOv2 was
                                trained
                                to match images and guess patches, not segment images.
                            </div>

                            <div className={styles.paragraph}>
                                Now look at the leftmost column: it consists of “things which have wings”. DINOv2
                                generates
                                red features for ‘bodies’, green features for ‘the leading edge of the wing’, blue
                                features
                                for ‘the trailing edge of the wing’, and purple features for ‘tails’. Out of nowhere,
                                the
                                model has figured out that airplanes and birds are structurally similar, and finds the
                                correspondence between the images.

                            </div>

                            <div className={styles.paragraph}>
                                Similarly, the second column is ‘elephants’. DINOv2 correctly learns that the first
                                three
                                images are elephants, including the second image which is from a difficult angle. What
                                is
                                more remarkable is it learns that the final image, a stylized statue of an elephant, is
                                in
                                fact an elephant, and furthermore correctly finds correspondences between the trunk,
                                ears,
                                body, and legs of the statue and the real elephants.

                            </div>
                            <img
                                src={img2}
                                alt="img2"
                                style={{objectFit: "contain", width: '100%',marginBottom:'20px', marginTop:'10px'}}
                            />
                            <div className={styles.paragraph}>
                                Here’s why these features are awesome. Because they correspond to semantically
                                meaningful
                                components of the image in an elegant way, it is easy to use them for tasks such as
                                monocular depth prediction (center image) or object segmentation (right image). All we
                                need
                                is a simple linear model (we scale, shift, and add the features) to transform these
                                representations into something which is useful for a lot of real-world applications.

                            </div>


                            {/* Why do we care? */}
                            <div className={`${styles.paragraph} ${styles.subparagraph}`}>Why do we care?</div>
                            <div className={styles.paragraph}>
                                Robotics is a label-deficient space. We can easily scale huge video datasets, but it is
                                difficult and time-consuming to generate fine-grained video labels which capture the
                                full
                                richness of the underlying data. We need to rely on the model itself to learn the
                                underlying
                                features, rather than rely on humans, who are expensive, unreliable, and biased, to
                                write
                                those features down.

                            </div>

                            <div className={styles.paragraph}>
                                Fortunately, there is a clever way to do this: we pose feature extraction as a video
                                compression problem. It turns out most useful robotics problems can be formulated as
                                “move
                                the robot, then have the robot move some objects”. In order to learn the sequence of
                                actions
                                that solves the problem, the model needs to be able to extract motion from training
                                data.

                            </div>

                            <div className={styles.paragraph}>
                                Readers who know a bit about video compression can probably see where this is going.
                                Codecs
                                like H265 which are used to transmit videos over the web compress video 200x or more by
                                estimating the motion between consecutive frames. In the H26x family, this is done by
                                subdividing frames into 8x8 or 16x16 blocks, performing block matching to find the best
                                matched blocks and offset between two frames, differencing the block-estimated frame
                                with
                                the real frame, and compressing the error (which is hopefully close to zero in most
                                places,
                                therefore low in information).

                            </div>

                            <div className={styles.paragraph}>
                                Unfortunately, this motion estimate is not useful for training AI models: it is not
                                variationally stable (small, random errors introduced into the compressed data stream
                                result
                                in catastrophic changes in the decompressed video) and the motion is predicted at the
                                block
                                level, which is not semantically meaningful. The former is important because AI
                                algorithms
                                learn approximations of their inputs, and these approximations may contain small errors.
                                The
                                latter is important because we are applying the motion features to a downstream task (a
                                robot interacting with the real world), not using them to decompress videos, so they
                                need to
                                somehow correspond to reality.

                            </div>


                            <div className={`${styles.paragraph} ${styles.subparagraph}`}>Peeling Bananas and Learning
                                Motion
                            </div>

                            <div className={styles.paragraph}>
                                Consider a man peeling bananas, and imagine for a moment you are a resident of Britain
                                in
                                1850 who wants to get a head start on the tech industry by building the worlds’ first
                                video
                                sharing platform. The only problem is, the internet doesn’t exist yet, and neither do
                                videos, so you’re stuck distributing stacks of glass plates by horse to your users.
                                Quickly,
                                you discover that the British horse network lacks the bandwidth you need, so you
                                investigate
                                ways to compress the videos.

                            </div>

                            <div className={styles.paragraph}>
                                “Easy!”, you think. “We’ll just send a photo and a description.” You pivot to
                                distributing a
                                photo of a man with a banana, and the description “the man is peeling the banana”.
                                Unfortunately, this is 1850s Britain, and bananas won’t be invented until 1888, so none
                                of
                                your users know what bananas are, let alone how to peel them.

                            </div>

                            <div className={styles.paragraph}>
                                Faced with tanking user retention, you improve the compression algorithm. The compressed
                                video is now a photo and the description “the man grabs the stem at the top, breaks it,
                                and
                                pulls the skin down. he then grabs the remaining flap of skin two times to remove all
                                the
                                peel sections”. This is a success: users now have enough information to visualize the
                                banana
                                peeling process.

                            </div>

                            <div className={styles.paragraph}>
                                With efficient distribution infrastructure in place, users flock to your platform.
                                Everyone
                                is excited about this new ‘banana’ phenomenon, and banana uploads skyrocket. This poses
                                a
                                new problem: the descriptions are not accurate enough to reconstruct different banana
                                videos
                                and capture all of their uniqueness. In order to capitalize on the invention of viral
                                banana
                                peeling videos, you improve the compression algorithm further: the description now reads
                                “the man grabs the stem at the top 4 mm down, holds it for 1 second, snaps it downwards
                                to a
                                45 degree angle over the course of two seconds. he then grabs the bar and over the
                                course of
                                the next four seconds, pulls the flap of skin down to 40% of the way from the top. the
                                process is repeated twice with the remaining skin”. With these new fine-grained
                                descriptions, users are now able to reconstruct videos with precision and enjoy many
                                distinct banana-peeling experiences.

                            </div>

                            <div className={styles.paragraph}>
                                See what’s happening here? The best way to compress the banana video also generates
                                instructions on how to peel bananas. In a nutshell, that is the link between video
                                compression and robotics:<span style={{fontWeight: 'bold'}}> compact ways to compress video have emergent features that
                            correspond to real-world, physical actions.</span>

                            </div>

                            <div className={styles.paragraph}>
                                Astute readers will have observed something else: it is surprisingly difficult to
                                explain
                                how to peel a banana. English is a great way to tell a story, but isn’t the densest or
                                clearest way to describe spatiotemporal features. That’s where the learning comes in:
                                rather
                                than fixing the vocabulary as “all of the words in the English language”, we can learn
                                the
                                “best” vocabulary to represent objects and actions.

                            </div>

                            <div className={styles.paragraph}>
                                Enough about bananas, let’s reduce this to implementation.

                            </div>

                            {/* Putting it all together */}
                            <div className={`${styles.paragraph} ${styles.subparagraph}`}>Putting It All Together
                            </div>
                            <img
                                src={img3}
                                alt="img3"
                                style={{objectFit: "contain", width: '100%', marginBottom:'20px', marginTop:'10px'}}
                            />
                            <div className={styles.paragraph}>
                                Here’s how we make this all happen in practice. We start with two frames from a video,
                                which
                                we will call “Before” and “After”. Before and After are sent through a black box, the
                                encoder, which outputs a list of feature vectors. These feature vectors are quantized:
                                there
                                is a fixed vocabulary of vectors (the codebook) and we replace each feature with the
                                nearest
                                vector in the codebook. The codebook changes as training progresses, but is shared
                                across
                                all of the training data. The quantization process creates an information bottleneck
                                that
                                forces the encoder to learn a compact representation.
                            </div>
                            <div className={styles.paragraph}>
                                Then, we add a little noise to the quantized vectors to encourage variational stability
                                (actually, the feature vectors represent the mean and standard deviation of a Gaussian
                                distribution from which a random sample is drawn). The resulting noisy features, along
                                with
                                the Before image, are sent through a decoder, which tries to use them to generate the
                                After
                                image. The training process tries to match the real After image and the decoded one as
                                well
                                as possible.
                            </div>

                            <div className={styles.paragraph}>
                                Finally, the feature vectors from the encoder are used as a motion representation for
                                further model training. Because motion extraction looks like video compression, the
                                features
                                we’ve learned from this process end up matching pretty well with real, manually labeled
                                motion collected from real robots, allowing us to scale up training by training on human
                                videos, not just robot videos, which are scarce and very costly to collect.
                            </div>
                        </div>

                    </main>)}
            </div>
        </div>
        {showQuizModal && (
            <QuizModal
                onClose={handleCloseQuizModal}
                hasCompletedQuiz={hasCompletedQuiz}
                quizPoints={quizPoints}
            />
        )}


    </div>);
}